import type { StationState, PositionRotation, AmpStateFromSse } from "@/types"
import { create } from "zustand"

interface UnitState {
  motor_connected: boolean
  relays: Record<string, number>
  sdrs: Record<string, number>
  pan?: number
  tilt?: number
  tilt_offset?: number
  pan_offset?: number
  timer?: {
    sdrs: Record<string, number>
    relays: number
  }
  // Temperature and alarms per amplifier (keyed by SDR/amp id)
  amp_data?: Record<
    string,
    {
      temp_alarm: number
      VSWR_alarm: number
      temp: number
      alarm?: number
    }
  >
  // Unit temperature sensors by floor
  floor_temps?: Record<string, number>
  // Live amplifier state snapshot from unit SSE, keyed by amplifier config name
  amp_states?: Record<string, AmpStateFromSse | null>
  real_units?: string[]
}

interface UnitsStore {
  units: Record<string, UnitState>
  isConnected: boolean

  // Replace or update a single unit's state
  updateUnitState: (unitId: string, newState: StationState | null) => void

  // Update a specific relay value
  setRelayValue: (unitId: string, relayId: string, value: number) => void

  // Update a specific SDR value
  setSdrValue: (unitId: string, sdrId: string, value: number) => void

  // Set connection status
  setConnectionStatus: (connected: boolean) => void

  // Optional per-unit pan/tilt updates
  getPanTilt: (serial_number: string) => PositionRotation

  // Optional per-unit motor_connected
  getMotorConnected: (serial_number: string) => boolean

  // Getters
  getRelaysForUnit: (unitId: string) => Record<string, number>
  getUnitSdrs: (unitId: string) => Record<string, number>
  getAmplifierStatus: (
    unitId: string,
    ampfConfName: string,
  ) => number | undefined

  // Set amplifier status manually (for pending state during activation)
  setAmplifierStatus: (
    unitId: string,
    ampfConfigName: string,
    value: number,
  ) => void

  // Batch optimistic overrides (key = "unitSerialNumber:configName") so UI keeps loading 3s even when SSE overwrites units
  optimisticAmplifierOverrides: Record<string, number>
  setOptimisticAmplifierOverrides: (next: Record<string, number>) => void
  upsertOptimisticAmplifierOverride: (key: string, value: number) => void
  clearOptimisticAmplifierOverride: (key: string) => void

  // Temperature getters
  getAmplifierTemp: (unitId: string, sdrId: string) => number | undefined
  getFloorTemps: (unitId: string) => Record<string, number>

  // Timer getters
  getSdrTimer: (unitId: string, sdrId: string) => number | undefined
  getRelaysTimer: (unitId: string) => number | undefined
  getAllTimers: (
    unitId: string,
  ) => { sdrs: Record<string, number>; relays: number } | undefined
  getRealUnits: (serial_number: string) => string[]
}

const normalizeRealUnits = (realUnits: unknown): string[] | undefined => {
  if (Array.isArray(realUnits)) {
    return realUnits.filter((item): item is string => typeof item === "string")
  }

  if (realUnits && typeof realUnits === "object") {
    return Object.values(realUnits).filter(
      (item): item is string => typeof item === "string",
    )
  }

  return undefined
}

const mergePanTilt = (
  incoming: unknown,
  previous: number | undefined,
): number | undefined => {
  if (incoming === null || typeof incoming === "undefined") {
    return previous
  }

  if (typeof incoming === "number" && Number.isFinite(incoming)) {
    return incoming
  }

  return previous
}

export const useUnitsStore = create<UnitsStore>((set, get) => ({
  units: {},
  isConnected: false,

  updateUnitState: (unitId, newState) => {
    if (newState === null) {
      set((state) => {
        const { [unitId]: _, ...rest } = state.units
        return { units: rest }
      })
      return
    }
    const hasRealUnits = Object.prototype.hasOwnProperty.call(
      newState,
      "real_units",
    )
    const normalizedRealUnits = normalizeRealUnits(newState.real_units)

    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          relays: newState.relays
            ? { ...newState.relays }
            : state.units[unitId]?.relays || {},
          sdrs: newState.sdrs
            ? { ...newState.sdrs }
            : state.units[unitId]?.sdrs || {},
          pan: mergePanTilt(newState.pan, state.units[unitId]?.pan),
          tilt: mergePanTilt(newState.tilt, state.units[unitId]?.tilt),
          tilt_offset: newState.tilt_offset ?? state.units[unitId]?.tilt_offset,
          pan_offset: newState.pan_offset ?? state.units[unitId]?.pan_offset,
          motor_connected:
            typeof newState.motor_connected === "boolean"
              ? newState.motor_connected
              : (state.units[unitId]?.motor_connected ?? false),

          amp_data: newState.amp_data
            ? { ...newState.amp_data }
            : state.units[unitId]?.amp_data,
          floor_temps: newState.floor_temps
            ? { ...newState.floor_temps }
            : state.units[unitId]?.floor_temps,
          amp_states: newState.amp_states
            ? { ...newState.amp_states }
            : state.units[unitId]?.amp_states,
          real_units: hasRealUnits
            ? (normalizedRealUnits ?? [])
            : state.units[unitId]?.real_units,
        },
      },
    }))
  },

  setRelayValue: (unitId, relayId, value) =>
    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          ...state.units[unitId],
          relays: {
            ...state.units[unitId]?.relays,
            [relayId]: value,
          },
        },
      },
    })),

  setSdrValue: (unitId, sdrId, value) =>
    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          ...state.units[unitId],
          sdrs: {
            ...state.units[unitId]?.sdrs,
            [sdrId]: value,
          },
        },
      },
    })),

  setConnectionStatus: (connected) => set({ isConnected: connected }),

  getPanTilt: (serial_number: string) => {
    const unit = get().units[serial_number]

    return {
      pan: unit?.pan ?? 0,
      tilt: unit?.tilt ?? 0,
      tilt_offset: unit?.tilt_offset ?? 0,
      pan_offset: unit?.pan_offset ?? 0,
    }
  },

  getMotorConnected: (serial_number: string) => {
    const unit = get().units[serial_number]
    return unit?.motor_connected ?? false
  },

  getRelaysForUnit: (unitId) => {
    return get().units[unitId]?.relays || {}
  },

  getUnitSdrs: (unitId) => {
    // console.log("getting")

    return get().units[unitId]?.sdrs || {}
  },

  getAmplifierStatus: (unitId, ampfConfigName) => {
    const val = get().units[unitId]?.sdrs?.[ampfConfigName]
    // Debug log for amplifier status lookup
    return val
  },

  setAmplifierStatus: (
    unitId: string,
    ampfConfigName: string,
    value: number,
  ) => {
    set((state) => ({
      units: {
        ...state.units,
        [unitId]: {
          ...state.units[unitId],
          sdrs: { ...state.units[unitId]?.sdrs, [ampfConfigName]: value },
        },
      },
    }))
  },

  optimisticAmplifierOverrides: {},
  setOptimisticAmplifierOverrides: (next) =>
    set({ optimisticAmplifierOverrides: next }),
  upsertOptimisticAmplifierOverride: (key, value) =>
    set((state) => ({
      optimisticAmplifierOverrides: {
        ...state.optimisticAmplifierOverrides,
        [key]: value,
      },
    })),
  clearOptimisticAmplifierOverride: (key) =>
    set((state) => {
      if (!(key in state.optimisticAmplifierOverrides)) {
        return state
      }
      const { [key]: _, ...rest } = state.optimisticAmplifierOverrides
      return { optimisticAmplifierOverrides: rest }
    }),

  getAmplifierTemp: (unitId, sdrId) => {
    const isVirtual = unitId.split("_")[0] == "virtual"
    if (isVirtual) return
    const temp = get().units[unitId]?.amp_data?.[sdrId]?.temp

    return temp
  },

  getFloorTemps: (unitId) => {
    const temps = get().units[unitId]?.floor_temps || {}

    return temps
  },

  getSdrTimer: (unitId, sdrId) => {
    return get().units[unitId]?.timer?.sdrs?.[sdrId]
  },

  getRelaysTimer: (unitId) => {
    return get().units[unitId]?.timer?.relays
  },

  getAllTimers: (unitId) => {
    const unit = get().units[unitId]
    return unit?.timer
      ? {
          sdrs: unit.timer.sdrs,
          relays: unit.timer.relays,
        }
      : undefined
  },
  getRealUnits: (serial_number: string) => {
    return get().units[serial_number]?.real_units || []
  },
}))
